// Copyright ©️ 2026 eVolve MEP, LLC

/** Reading raw geometry out of the viewer's fragment list. Nothing here knows what a rack is. */

import type { AutodeskFragmentList, FragmentId } from 'components/Autodesk/extensions/viewerHelpers';
import { THREE, toVector3 } from 'components/Autodesk/viewerThree';
import { isNil } from 'helpers/isNotNil';

/** A length below this is treated as zero rather than measured or normalized. */
export const MIN_MEASURABLE_LENGTH_FT = 1e-6;

/** Wrapper around {@link AutodeskFragmentList.getWorldBounds} that returns a `Box3`*/
export const getFragmentWorldBounds = (fragList: AutodeskFragmentList, fragId: FragmentId) => {
  const worldBounds = new THREE.Box3();
  fragList.getWorldBounds(fragId, worldBounds);
  return worldBounds;
};

/** Shorthand for the viewer's own vertex-buffer readers */
const { enumMeshVertices, enumMeshLines, enumMeshTriangles } = Autodesk.Viewing.Private.VertexEnumerator;

/** Wrapper around {@link Autodesk.Viewing.Private.VertexEnumerator.enumMeshVertices} that takes a fragment */
export const enumFragmentVertices = (
  fragList: AutodeskFragmentList,
  fragId: number,
  visit: (worldPoint: THREE.Vector3) => void,
) => {
  const geometry = fragList.getGeometry?.(fragId);
  if (isNil(geometry)) return;

  const worldMatrix = new THREE.Matrix4();
  fragList.getWorldMatrix(fragId, worldMatrix);

  enumMeshVertices(geometry, visit, worldMatrix);
};

/** One triangle of a fragment's mesh, in world space. Always encloses some area. */
export type WorldTriangle = { a: THREE.Vector3; b: THREE.Vector3; c: THREE.Vector3 };

/**
 * Whether a triangle encloses any area — its cross product being twice that area.
 *
 * A vertex buffer carries collapsed triangles: a quad squared off at one corner, a repeated index. They
 * have no normal, and no surface for a ray to cross, so nothing downstream can do anything with one.
 */
const triangleHasArea = ({ a, b, c }: WorldTriangle): boolean =>
  b.clone().sub(a).cross(c.clone().sub(a)).length() >= MIN_MEASURABLE_LENGTH_FT;

/**
 * Every triangle of a fragment's solid mesh that encloses an area, in world space. Empty when the fragment
 * holds line geometry instead, which `enumMeshTriangles` would otherwise walk and report as triangles.
 *
 * Unlike `enumMeshVertices`, `enumMeshTriangles` applies no matrix of its own and reuses its 3 corner
 * vectors between visits, so each corner is transformed and copied out here.
 */
export const getFragmentWorldTriangles = (fragList: AutodeskFragmentList, fragId: FragmentId): WorldTriangle[] => {
  const geometry = fragList.getGeometry?.(fragId);
  if (isNil(geometry) || geometry.isLines) return [];

  const worldMatrix = new THREE.Matrix4();
  fragList.getWorldMatrix(fragId, worldMatrix);
  const toWorld = (corner: THREE.Vector3) => toVector3(corner).applyMatrix4(worldMatrix);

  const triangles: WorldTriangle[] = [];
  enumMeshTriangles(geometry, (a, b, c) => {
    const triangle = { a: toWorld(a), b: toWorld(b), c: toWorld(c) };
    if (triangleHasArea(triangle)) triangles.push(triangle);
  });
  return triangles;
};

/** A pair of world-space points — one line of a fragment's line geometry. */
export type FragmentLineSegment = { start: THREE.Vector3; end: THREE.Vector3 };

/** Wrapper around {@link Autodesk.Viewing.Private.VertexEnumerator.enumMeshLines} that takes a fragment */
const enumFragmentLines = (
  fragList: AutodeskFragmentList,
  fragId: number,
  visit: (start: THREE.Vector3, end: THREE.Vector3) => void,
) => {
  const geometry = fragList.getGeometry?.(fragId);
  if (isNil(geometry) || !geometry.isLines) return;
  // Both endpoints are reused between visits, and `enumMeshLines` applies no matrix of its own.
  enumMeshLines(geometry, visit);
};

/**
 * The world-space lines of a fragment's line geometry. Empty when the fragment holds none.
 *
 * The `isLines` guard matters: `enumMeshLines` walks a solid mesh's buffer just as willingly and reports
 * its triangles as lines. Checked for truthiness, since the viewer does not promise a real boolean.
 */
export const getFragmentLineSegments = (fragList: AutodeskFragmentList, fragId: number): FragmentLineSegment[] => {
  const geometry = fragList.getGeometry?.(fragId);
  if (isNil(geometry) || !geometry.isLines) return [];

  const worldMatrix = new THREE.Matrix4();
  fragList.getWorldMatrix(fragId, worldMatrix);

  const segments: FragmentLineSegment[] = [];
  enumFragmentLines(fragList, fragId, (start, end) =>
    segments.push({
      start: start.clone().applyMatrix4(worldMatrix),
      end: end.clone().applyMatrix4(worldMatrix),
    }),
  );

  return segments;
};

/** The viewer's bundled `Box3` erroneously types `intersectsBox` as returning the literal `true`. */
export const boxesIntersect = (box: THREE.Box3, other: THREE.Box3): boolean => box.intersectsBox(other);

/**
 * How far along a ray it crosses a triangle, or `null` when it never does. Front and back faces alike, and
 * only ahead of the ray's origin. The direction must be a unit vector for the result to be a distance.
 *
 * The viewer's bundled `Ray` erroneously types `intersectTriangle` as always returning a point, so the miss
 * has to be typed back in before it can be read. It builds that point only once it has one to report, so
 * the misses — nearly every call, over a whole mesh — cost nothing.
 */
export const rayCrossesTriangleAtFt = (ray: THREE.Ray, { a, b, c }: WorldTriangle): number | null => {
  const crossing: THREE.Vector3 | null = ray.intersectTriangle(a, b, c, false);
  return isNil(crossing) ? null : ray.origin.distanceTo(crossing);
};
